import {
  del,
  get,
  patch,
  post,
  put,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  DonationStatusUpdatePayload,
  ItemDonationPayload,
  ItemDonationRecord,
  PickupUpdateEmailResult,
  PickupUpdatePayload,
} from "@project/types/item-donation";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  isApiItemResponse,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/item-donations`;
const SILENT: RequestConfig = { showNotification: false };

type PickupUpdateApiResponse =
  | (ApiItemResponse<ItemDonationRecord> & {
      email?: PickupUpdateEmailResult;
    })
  | ItemDonationRecord;

function withDonorSent(
  item: ItemDonationRecord | undefined,
  email?: PickupUpdateEmailResult
): ItemDonationRecord | undefined {
  if (!item) {
    return undefined;
  }
  if (email?.donor_sent === undefined && item.donor_sent !== undefined) {
    return item;
  }
  return {
    ...item,
    donor_sent: email?.donor_sent ?? item.donor_sent ?? null,
  };
}

class AdminItemDonationService {
  listPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<ItemDonationRecord>> =>
    fetchListPage<ItemDonationRecord>(`${BASE}/`, query);

  get = async (id: number): Promise<ItemDonationRecord | undefined> => {
    const data = await get<
      ApiItemResponse<ItemDonationRecord> | ItemDonationRecord
    >(`${BASE}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  create = async (
    payload: ItemDonationPayload
  ): Promise<ItemDonationRecord> => {
    const data = await post<
      ApiItemResponse<ItemDonationRecord> | ItemDonationRecord,
      ItemDonationPayload
    >(`${BASE}/`, payload, SILENT);
    const item = unwrapItem(data);
    if (!item) {
      throw new Error("Failed to create in-kind donation request.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<ItemDonationPayload>
  ): Promise<ItemDonationRecord | undefined> => {
    const data = await patch<
      ApiItemResponse<ItemDonationRecord> | ItemDonationRecord,
      Partial<ItemDonationPayload>
    >(`${BASE}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };

  updateStatus = async (
    id: number,
    payload: DonationStatusUpdatePayload
  ): Promise<ItemDonationRecord | undefined> => {
    const data = await put<
      ApiItemResponse<ItemDonationRecord> | ItemDonationRecord,
      DonationStatusUpdatePayload
    >(`${BASE}/${id}/update_status/`, payload, SILENT);
    return unwrapItem(data);
  };

  sendPickupUpdate = async (
    id: number,
    payload: PickupUpdatePayload
  ): Promise<ItemDonationRecord | undefined> => {
    const data = await put<PickupUpdateApiResponse, PickupUpdatePayload>(
      `${BASE}/${id}/send_pickup_update/`,
      payload,
      SILENT
    );
    const email =
      data && typeof data === "object" && "email" in data
        ? data.email
        : undefined;
    const item = isApiItemResponse<ItemDonationRecord>(data)
      ? data.data
      : (data as ItemDonationRecord | undefined);
    return withDonorSent(item, email);
  };
}

export const adminItemDonationService = new AdminItemDonationService();
